import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'

import TranslationNS from '@/utils/Constants/TranslationNS'
import { tzOffset } from '@/utils/DateHelpers'
import { useAuth } from '../../middlewares/auth/auth-context'
import { DASHBOARD_ICON } from '@/components/AdminDashboard/adminNav'
import { tourForRoute, useTourStore } from '@/components/Organizer/Tours'

const buzzersEmail = import.meta.env.VITE_BUZZERS_EMAIL

// Picker languages, shared by the desktop dropdown and the mobile sheet.
export const LANGUAGES = [
    { code: 'en', label: 'English' },
    { code: 'fr', label: 'Français' },
    { code: 'pt', label: 'Português' },
]

/**
 * Single source of truth for the navbar profile/admin menu, shared by the
 * desktop antd Dropdown and the mobile full-screen sheet so the item set
 * (incl. the admin / buzzers / partner conditionals and the unsaved-work guard)
 * is defined only once.
 *
 * Returns:
 *  - `menuItems`        primitive rows for the mobile sheet: { key, label, to } | { type:'divider', key }
 *  - `profileMenuItems` antd Dropdown shape for the desktop dropdown (incl. user/time header + className map)
 *  - `externalLinks`    Our Services / Help & FAQ (external <a>)
 *  - `guardedNavigate` / `guardedDisconnect` honour the `processInProgress` guard via window.confirm
 */
export default function useNavMenuItems({ processInProgress, setProcessInProgess }) {
    const { t } = useTranslation(TranslationNS.NAVBAR)
    const { t: tTours } = useTranslation(TranslationNS.TOURS)
    const navigate = useNavigate()
    const location = useLocation()
    const authContext = useAuth()
    const startTour = useTourStore((state) => state.start)
    // the guided tour of the current page, when it has one outside the organizer shell (home, My events)
    const siteTour = tourForRoute(location.pathname)

    // partner status lives in the auth context (fetched once per login, shared with the leaderboard)
    const isPartner = authContext.isPartner

    // Navigate from the desktop profile dropdown, honouring the unsaved-work guard.
    const guardedNavigate = (to) => {
        if (processInProgress) {
            if (!window.confirm(t('Are you sure you want to leave?'))) return
            setProcessInProgess(false)
        }
        navigate(to)
    }

    const guardedDisconnect = () => {
        if (processInProgress) {
            if (!window.confirm(t('Are you sure you want to leave?'))) return
            setProcessInProgess(false)
        }
        authContext.onLogout('/')
    }

    // The shared model: navigable rows + dividers (no user header — each surface
    // renders its own). `to` drives both the desktop onClick and the mobile NavLinkConfirm.
    // "Create an Event" is deliberately absent: the side menu and the home category bar show it as a
    // button; the desktop bar no longer does (15 Oct 2026).
    // A row with `action` runs it instead of navigating (the guided tour of the page, 29 Oct 2026).
    const menuItems = [
        { key: 'my-events', label: t('My Events'), to: '/my-events' },
        { key: 'my-account', label: t('My Account'), to: '/my-account' },
        ...(siteTour ? [{ key: 'tour', label: tTours('Guided tour'), action: () => startTour(siteTour.id) }] : []),
        ...(authContext.isAdmin
            ? [
                  { type: 'divider', key: 'divider-admin' },
                  {
                      key: 'admin-dashboard',
                      label: (
                          <span className="inline-flex items-center gap-2.5">
                              {DASHBOARD_ICON}
                              {t('Admin dashboard')}
                          </span>
                      ),
                      to: '/admin',
                      match: 'prefix',
                  },
              ]
            : []),
        ...(!authContext.isAdmin && authContext.user.email === buzzersEmail
            ? [{ key: 'buzzers', label: t('Buzzers results'), to: '/buzzers-results' }]
            : []),
        ...(isPartner ? [{ key: 'partner', label: t('External API config'), to: '/partner-external-api-config' }] : []),
        { type: 'divider', key: 'divider-disconnect' },
        { key: 'disconnect', label: t('Disconnect') },
    ]

    // Current route, marked on both surfaces (weight + field surface, never colour).
    const isCurrent = (it) => {
        if (!it.to) return false
        if (it.match === 'prefix') return location.pathname === it.to || location.pathname.startsWith(`${it.to}/`)
        return location.pathname === it.to
    }

    // Desktop antd Dropdown shape, derived from the same definition. Row styling
    // lives in ./index.css (.navbar-desktop__dropdown); only the current-route
    // marker is decided here.
    const profileMenuItems = menuItems.map((it) =>
        it.type === 'divider'
            ? { type: 'divider' }
            : {
                  key: it.key,
                  label: it.label,
                  className: isCurrent(it) ? 'navbar-desktop__dropdown-current' : undefined,
                  onClick: it.key === 'disconnect' ? guardedDisconnect : it.action || (() => guardedNavigate(it.to)),
              },
    )

    const externalLinks = [
        { key: 'services', label: t('Our Services'), href: 'https://info.scoring.fit/' },
        { key: 'faq', label: t('Help & FAQ'), href: 'https://help.scoring.fit/' },
    ]

    return { menuItems, profileMenuItems, externalLinks, isCurrent, guardedNavigate, guardedDisconnect, isPartner }
}
